const u = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMG = {
  rawWood: u("1632359552106-2599db65f51c"),
  decking: u("1573869908170-64b53a60d8da"),
  door: u("1559006863-03e0799b836a"),
  doorGlass: u("1659720879327-827462ca3942"),
  bench: u("1786175087690-8703dae2bcdd"),
  outdoorBench: u("1768179123254-7e3abb5a571f"),
  rainforest: u("1658167210874-f3679e784425"),
  forest: u("1473448912268-2022ce9509d8"),
  sunTrees: u("1518495973542-4542c06a5843"),
  dining: u("1519710164239-da123dc03ef4"),
  table: u("1524758631624-e2822e304c36"),
  chair: u("1586023492125-27b2c045efd7"),
  armchair: u("1592078615290-033ee584e267"),
  lounge: u("1493663284031-b7e3aefcae8e"),
  interior: u("1600607687939-ce8a6c25118c"),
  house: u("1600585154340-be6161a56a0c"),
};

export const NAV = [
  { label: "Timber", to: "/timber" },
  { label: "Doors", to: "/doors" },
  { label: "Furniture", to: "/furniture" },
  { label: "Shop", to: "/shop" },
  { label: "Kwila / Merbau", to: "/kwila-merbau" },
  { label: "Trade", to: "/trade" },
  { label: "About", to: "/about" },
  { label: "Guides", to: "/guides" },
  { label: "Contact", to: "/contact" },
];

export const DIVISIONS = [
  {
    num: "01",
    key: "timber",
    name: "Timber",
    path: "/timber",
    image: "/vitex-decking.png",
    tag: "Kiln-dried & structural supply",
    copy: "Kiln-dried sawn timber, structural timber, decking, posts and hardwood species for joiners, builders, merchants and manufacturers.",
  },
  {
    num: "02",
    key: "doors",
    name: "Doors",
    path: "/doors",
    image: "/door-walls.png",
    tag: "Solid & custom hardwood doors",
    copy: "Solid timber doors and custom hardwood door options for builders, renovators, designers, specifiers and trade buyers.",
  },
  {
    num: "03",
    key: "furniture",
    name: "Furniture",
    path: "/furniture",
    image: "/furniture-card.png",
    tag: "Kwila outdoor living",
    copy: "Kwila outdoor furniture including tables, chairs, benches, settings, sunloungers and accessories — built for Australian outdoor living.",
  },
];

export const CATEGORIES = {
  timber: [
    "Kiln-Dried Sawn Timber",
    "Green Sawn Timber",
    "Structural Timber",
    "Decking",
    "Posts",
    "Joinery Timber",
    "Door Manufacturing Timber",
    "Timber Packs & Bulk",
  ],
  doors: [
    "Solid Timber Doors",
    "External Doors",
    "Internal Doors",
    "Custom Doors",
    "Door Slabs & Blanks",
    "Door Components",
    "Builder & Specifier Supply",
  ],
  furniture: ["Tables", "Chairs", "Benches & Settings", "Accessories", "Dining Settings", "Sunloungers"],
};

export const SPECIES_FILTERS = [
  "Kwila (Merbau)",
  "Taun",
  "Wau Beech",
  "Masawa",
  "Vitex",
  "Rosewood",
  "Mixed Tropical Hardwood",
];

export const SPECIES = [
  {
    slug: "kwila-merbau",
    name: "Kwila / Merbau",
    latin: "Intsia bijuga",
    tone: "Golden to dark brown with yellow and black flecks; interlocked grain, coarse but even texture",
    blurb:
      "Kwila, also known as Merbau, Afzelia, Borneo Teak, Ipil (Philippines) or Ironwood (UK), is a premium tropical hardwood valued for its strength, density, durability and rich natural colour. Recorded hardness of 8.6 compares with Jarrah at 8.5 and Teak at 4.5 [historical figures — to be confirmed by client].",
    uses: ["Outdoor furniture", "Decking", "Solid timber doors", "Flooring", "Joinery", "Structural and decorative timber"],
  },
  {
    slug: "taun",
    name: "Taun",
    latin: "Pometia pinnata",
    tone: "Light to medium brown with a warm, even grain",
    blurb:
      "Taun is a versatile PNG hardwood commonly used for joinery, furniture components and general construction.",
    uses: ["Joinery", "Furniture components", "Interior fit-out", "General construction"],
  },
  {
    slug: "wau-beech",
    name: "Wau Beech",
    latin: "[Botanical name to be confirmed by client]",
    tone: "Pale straw to light brown",
    blurb:
      "Wau Beech is a lighter-toned PNG hardwood suited to interior applications and furniture manufacturing.",
    uses: ["Interior joinery", "Furniture", "Mouldings", "Panelling"],
  },
  {
    slug: "masawa",
    name: "Masawa",
    latin: "[Botanical name to be confirmed by client]",
    tone: "Medium brown with warm undertones",
    blurb:
      "Masawa is a PNG hardwood used across joinery and general timber applications. Detailed species information is being prepared.",
    uses: ["Joinery", "General timber applications", "Furniture components"],
  },
  {
    slug: "vitex",
    name: "Vitex",
    latin: "Vitex cofassus",
    tone: "Grey-brown to olive-brown, fine and even texture",
    blurb:
      "Vitex is a fine-textured hardwood valued for outdoor and marine-adjacent applications, joinery and construction.",
    uses: ["Decking", "Joinery", "Boat and outdoor applications", "Construction"],
  },
  {
    slug: "rosewood",
    name: "Rosewood",
    latin: "Pterocarpus indicus [to be confirmed by client]",
    tone: "Golden to rich red-brown, often highly figured",
    blurb:
      "PNG Rosewood is a decorative hardwood prized for figured grain and rich colour in premium joinery and furniture.",
    uses: ["Decorative joinery", "Fine furniture", "Feature panelling", "Craft and turning"],
  },
];

export const GUIDES = [
  {
    slug: "kwila-care",
    title: "How to Care for Kwila Outdoor Furniture",
    tag: "Care & Maintenance",
    readTime: "4 min read",
    excerpt:
      "Oiling, cleaning and placement — the simple routine that keeps Kwila outdoor furniture looking rich for decades.",
    image: IMG.outdoorBench,
    sections: [
      {
        h: "Before you position your furniture",
        p: [
          "Fresh Kwila can release natural tannins when wet. Before placing new furniture on paving, tiles or concrete, hose it down thoroughly and allow it to weather in a suitable area such as grass or gravel until the tannin release settles.",
        ],
      },
      {
        h: "Oiling your Kwila furniture",
        p: [
          "Regular oiling keeps Kwila's rich red-brown colour vibrant and protects the surface. Depending on exposure and conditions, oiling around every three months may be recommended. [Final care product recommendations and intervals to be confirmed by client.]",
          "Always clean the surface first and allow it to dry fully before applying oil with a brush or cloth, following the grain.",
        ],
      },
      {
        h: "Cleaning",
        p: [
          "Wash down with mild soapy water and a soft brush. Avoid high-pressure washing at close range, which can raise the grain.",
        ],
      },
      {
        h: "Canvas and cushions",
        p: [
          "Canvas slings and cushions should be brushed down and spot-cleaned. Canvas is UV fade, mildew and rot resistant, and canvas products carry a 1 year guarantee. [Final canvas warranty wording to be approved by client.]",
        ],
      },
    ],
  },
  {
    slug: "tannin-bleed",
    title: "What is Tannin Bleed?",
    tag: "Know Your Timber",
    readTime: "3 min read",
    excerpt:
      "Why new Kwila releases a reddish stain when wet — and the simple steps to manage it before it touches your paving.",
    image: IMG.decking,
    sections: [
      {
        h: "A natural process",
        p: [
          "Kwila is rich in natural tannins. When fresh Kwila gets wet, these tannins can leach out and temporarily stain nearby paving, tiles or concrete. This is completely natural and settles as the timber weathers.",
        ],
      },
      {
        h: "What to do",
        p: [
          "Before placing new Kwila furniture or timber in its final position, hose it down and allow it to dry out — twice, and more if necessary — on grass or gravel until the tannin run-off clears.",
          "If staining occurs on paving, most marks fade with weathering and can be treated with appropriate cleaners. Test a small area first.",
        ],
      },
    ],
  },
  {
    slug: "kwila-vs-merbau",
    title: "Kwila vs Merbau: Are They the Same Timber?",
    tag: "Know Your Timber",
    readTime: "2 min read",
    excerpt: "Yes — both names refer to Intsia bijuga, the same premium tropical hardwood species.",
    image: IMG.rawWood,
    sections: [
      {
        h: "One species, two names",
        p: [
          "Kwila and Merbau are two common names for the same species: Intsia bijuga. 'Kwila' is the name most used in Papua New Guinea, while 'Merbau' is common in Australian and Asian markets.",
        ],
      },
      {
        h: "Why it matters",
        p: [
          "If you have used Merbau decking or furniture before, Kwila is the same timber — valued for its density, durability, termite resistance and rich colour.",
        ],
      },
    ],
  },
  {
    slug: "solid-timber-doors",
    title: "Solid Timber Door Buying Guide",
    tag: "Doors",
    readTime: "5 min read",
    excerpt:
      "Internal vs external, species, thickness and finish — what to consider before ordering a solid hardwood door.",
    image: IMG.door,
    sections: [
      {
        h: "Internal or external?",
        p: [
          "External doors need denser species, appropriate thickness and a finish suited to weather exposure. Kwila's natural durability makes it a strong choice for entry doors. [Compliance and rating details to be confirmed by client.]",
        ],
      },
      {
        h: "Dimensions and thickness",
        p: [
          "Confirm your opening size, door thickness and any hardware or pre-hanging requirements before requesting a quote. Custom sizes are available for made-to-order doors.",
        ],
      },
      {
        h: "Finish",
        p: [
          "Doors can be supplied unfinished for on-site coating, or with a prepared finish. [Final finish options to be confirmed by client.]",
        ],
      },
    ],
  },
  {
    slug: "kiln-dried-hardwood",
    title: "Kiln-Dried Hardwood: What It Means for Timber Buyers",
    tag: "Timber Supply",
    readTime: "4 min read",
    excerpt:
      "Why kiln drying matters for joinery, doors and furniture — and what to ask your timber supplier.",
    image: IMG.forest,
    sections: [
      {
        h: "Why kiln drying matters",
        p: [
          "Kiln drying reduces timber moisture content in a controlled way, improving stability for joinery, door manufacturing and furniture making where movement must be minimised. [Moisture content specifications to be confirmed by client.]",
        ],
      },
      {
        h: "Green vs kiln-dried",
        p: [
          "Green (unseasoned) sawn timber suits applications where further drying or movement is acceptable. Kiln-dried stock is preferred for precision work. Mr Kwila supplies both, with kiln drying carried out in PNG.",
        ],
      },
      {
        h: "What to ask",
        p: [
          "Ask about species, grade, moisture content, available sizes, pack quantities and lead times — our team can quote to your cutting list.",
        ],
      },
    ],
  },
];

export const KWILA_FAQS = [
  {
    q: "What is Kwila?",
    a: "Kwila is the common Papua New Guinea name for Intsia bijuga, a premium tropical hardwood valued for its strength, durability and rich reddish-brown colour. It is used for outdoor furniture, decking, flooring, joinery, solid timber doors and structural timber.",
  },
  {
    q: "Is Kwila the same as Merbau?",
    a: "Yes. Kwila and Merbau are two names for the same species, Intsia bijuga. Kwila is the common name in Papua New Guinea; Merbau is the name most used in Australia and Asia.",
  },
  {
    q: "Is Kwila good for outdoor furniture?",
    a: "Yes. Kwila is dense, durable and naturally termite resistant, which makes it one of the most popular timbers for outdoor furniture in Australia. With simple care — cleaning and periodic oiling — Kwila furniture lasts for many years outdoors.",
  },
  {
    q: "Does Kwila bleed tannins?",
    a: "Fresh Kwila can release natural tannins when wet, which may temporarily stain paving, tiles or concrete. Hose new Kwila down and let it weather on grass or gravel before placing it in its final position.",
  },
  {
    q: "How do you maintain Kwila outdoor furniture?",
    a: "Clean with mild soapy water and a soft brush, allow to dry, and oil periodically — around every three months depending on exposure. See our care guide for the full routine.",
  },
  {
    q: "Is Kwila suitable for doors?",
    a: "Yes. Kwila's density, stability and rich colour make it an excellent choice for solid timber entry doors and internal doors. Custom sizes are available on request.",
  },
  {
    q: "Is Kwila suitable for decking?",
    a: "Yes. Kwila (Merbau) is one of Australia's most widely used decking timbers thanks to its durability and hard-wearing surface.",
  },
  {
    q: "How do I request a trade timber quote?",
    a: "Add products to your quote list or use the Request a Quote form with your species, dimensions, quantities and delivery location. Trade buyers can also apply for a trade account for priority pricing.",
  },
];

export const CARE_CARDS = [
  {
    title: "Oiling & Care",
    copy: "Keep the rich colour alive — when and how to oil your Kwila furniture.",
    to: "/guides/kwila-care",
    image: IMG.outdoorBench,
  },
  {
    title: "Tannin Bleed",
    copy: "What the reddish run-off is, and how to manage it before furniture meets paving.",
    to: "/guides/tannin-bleed",
    image: IMG.decking,
  },
  {
    title: "Kwila vs Merbau",
    copy: "Two names, one species — understand the timber behind the brand.",
    to: "/guides/kwila-vs-merbau",
    image: IMG.rawWood,
  },
];

export const PRODUCT_TYPES = [
  { label: "Outdoor Tables", to: "/shop?division=furniture&category=Tables", image: IMG.dining },
  { label: "Chairs", to: "/shop?division=furniture&category=Chairs", image: IMG.chair },
  { label: "Benches & Settings", to: "/shop?division=furniture&category=Benches & Settings", image: IMG.bench },
  { label: "Solid Timber Doors", to: "/shop?division=doors", image: IMG.door },
  { label: "Decking", to: "/shop?division=timber&category=Decking", image: IMG.decking },
  { label: "Timber Supply", to: "/shop?division=timber", image: IMG.rawWood },
];

export const BUYER_TYPES = [
  "Retail customer",
  "Builder",
  "Joiner / Cabinetmaker",
  "Door manufacturer",
  "Timber merchant",
  "Specifier / Designer",
  "Reseller",
  "Other",
];

export const MOULDINGS = [
  { name: "Tongue & Groove Flooring", sizes: ["90×19 mm", "60×19 mm"] },
  { name: "Tongue & Groove Double Vee", sizes: ["85×19 mm", "85×11.5 mm", "138×19 mm", "185×11.5 mm", "185×19 mm"] },
  { name: "Tongue & Groove Single Vee (Douglas Fir)", sizes: ["128×16 mm"] },
  { name: "T&G Double VJ", sizes: ["90×19 mm"] },
  { name: "Decking — Pencil Round", sizes: ["90×19 mm", "65×19 mm"] },
  { name: "French Light Sill", sizes: ["140×31 mm"] },
  { name: "Window Sill", sizes: ["140×41 mm"] },
  { name: "Weatherboard (Standard Chamfer)", sizes: ["140×19 mm", "90×19 mm"] },
  { name: "Skirtings", sizes: ["90×19 mm", "65×19 mm", "41×19 mm"] },
  { name: "Colonial Skirting — Type A", sizes: ["110×19 mm"] },
  { name: "Colonial Skirting — Type B", sizes: ["91×19 mm"] },
  { name: "Colonial Skirting — Type C", sizes: ["66×19 mm", "41×19 mm"] },
  { name: "Door Jambs & Framing", sizes: ["66×(41 or 30) mm", "92×(41 or 30) mm", "117×(41 or 30) mm", "138×(41 or 30) mm"] },
  { name: "Handrail — Bread Loaf, Flat Base", sizes: ["68×68 mm", "68×42 mm"] },
  { name: "Handrail — Bread Loaf, Single Slat", sizes: ["68×68 mm", "68×42 mm"] },
  { name: "Handrail — Ladies Waist, Flat Base", sizes: ["68×68 mm", "68×42 mm"] },
  { name: "Handrail — Ladies Waist, Single Slat", sizes: ["68×68 mm", "68×42 mm"] },
  { name: "Handrail — Ladies Waist", sizes: ["68×68 mm", "68×42 mm"] },
  { name: "Dowel Rods", sizes: ["10 mm", "12 mm", "16 mm", "19 mm", "22 mm", "25 mm", "32 mm", "38 mm", "50 mm", "60 mm"] },
  { name: "D-Mould", sizes: ["12×6 mm", "19×6 mm", "32×9 mm"] },
  { name: "Quad", sizes: ["12×12 mm", "19×19 mm", "32×32 mm"] },
  { name: "Angle Fillets", sizes: ["12×12 mm", "19×19 mm", "32×32 mm"] },
  { name: "External Corner Mould", sizes: ["19×19 mm", "32×32 mm", "41×41 mm"] },
  { name: "Cover Strips", sizes: ["19×12 mm", "32×12 mm", "42×12 mm", "70×12 mm"] },
  { name: "Square Dressed", sizes: ["12×12 mm", "19×19 mm", "32×32 mm"] },
  { name: "Posts", sizes: ["68×68 mm", "90×90 mm"] },
];

export const MOULDING_PROFILES = [
  {
    slug: "tongue-groove-flooring",
    name: "Tongue & Groove Flooring",
    img: "/mouldings/tg-flooring.png",
    sizes: ["90×19 mm", "60×19 mm"],
    desc: "Classic tongue and groove hardwood flooring boards, machined in our PNG mills for interior floors and feature linings.",
  },
  {
    slug: "tg-double-vee-85x19",
    name: "T&G Double Vee — 85×19",
    img: "/mouldings/tg-double-vee-85x19.png",
    sizes: ["85×19 mm"],
    desc: "Double vee tongue and groove profile for lining boards, wall panelling and ceilings.",
  },
  {
    slug: "tg-double-vee-85x115",
    name: "T&G Double Vee — 85×11.5",
    img: "/mouldings/tg-double-vee-85x115.png",
    sizes: ["85×11.5 mm"],
    desc: "Lighter-gauge double vee lining board profile for interior panelling.",
  },
  {
    slug: "tg-double-vee-wide",
    name: "T&G Double Vee — Wide Boards",
    img: "/mouldings/tg-double-vee-wide.png",
    sizes: ["138×19 mm", "185×19 mm"],
    desc: "Wide-board double vee profile for feature walls and ceilings.",
  },
  {
    slug: "tg-single-vee-douglas-fir",
    name: "T&G Single Vee — Douglas Fir",
    img: "/mouldings/tg-single-vee.png",
    sizes: ["128×16 mm"],
    desc: "Single vee tongue and groove profile milled in Douglas Fir.",
  },
  {
    slug: "tg-double-vj",
    name: "T&G Double VJ",
    img: "/mouldings/tg-double-vj.png",
    sizes: ["90×19 mm"],
    desc: "Double VJ tongue and groove lining profile for walls and ceilings.",
  },
  {
    slug: "decking-pencil-round",
    name: "Decking — Pencil Round",
    img: "/mouldings/decking-pencil-round.png",
    sizes: ["90×19 mm", "65×19 mm"],
    desc: "Pencil round hardwood decking boards — dense, durable and suited to Australian outdoor conditions.",
  },
  {
    slug: "french-light-sill",
    name: "French Light Sill",
    img: "/mouldings/french-light-sill.png",
    sizes: ["140×31 mm"],
    desc: "Stepped French light sill profile for door and window joinery.",
  },
  {
    slug: "window-sill",
    name: "Window Sill",
    img: "/mouldings/window-sill.png",
    sizes: ["140×41 mm"],
    desc: "Hardwood window sill profile with chamfered edge.",
  },
  {
    slug: "weatherboard-chamfer",
    name: "Weatherboard — Standard Chamfer",
    img: "/mouldings/weatherboard.png",
    sizes: ["140×19 mm", "90×19 mm"],
    desc: "Standard chamfer weatherboard cladding profile in durable hardwood.",
  },
  {
    slug: "skirtings",
    name: "Skirtings",
    img: "/mouldings/skirtings.png",
    sizes: ["90×19 mm", "65×19 mm", "41×19 mm"],
    desc: "Bevelled hardwood skirting boards for interior finishing.",
  },
  {
    slug: "colonial-skirting-type-a",
    name: "Colonial Skirting — Type A",
    img: "/mouldings/colonial-skirting-a.png",
    sizes: ["110×19 mm"],
    desc: "Colonial profile skirting with decorative ogee detail.",
  },
  {
    slug: "colonial-skirting-type-b",
    name: "Colonial Skirting — Type B",
    img: "/mouldings/colonial-skirting-b.png",
    sizes: ["91×19 mm"],
    desc: "Colonial profile skirting with a subtle ogee edge.",
  },
  {
    slug: "colonial-skirting-type-c",
    name: "Colonial Skirting — Type C",
    img: "/mouldings/colonial-skirting-c.png",
    sizes: ["66×19 mm", "41×19 mm"],
    desc: "Compact colonial profile skirting with decorative detailing.",
  },
  {
    slug: "door-jambs-framing",
    name: "Door Jambs & Framing",
    img: "/mouldings/door-jambs.png",
    sizes: ["66×(41 or 30) mm", "92×(41 or 30) mm", "117×(41 or 30) mm", "138×(41 or 30) mm"],
    desc: "Rebated door jamb and framing stock for door manufacturers and joiners.",
  },
  {
    slug: "handrail-bread-loaf-flat-base",
    name: "Handrail — Bread Loaf, Flat Base",
    img: "/mouldings/handrail-bread-loaf-flat.png",
    sizes: ["68×68 mm", "68×42 mm"],
    desc: "Bread loaf handrail profile with flat base for balustrades and stairs.",
  },
  {
    slug: "handrail-bread-loaf-single-slat",
    name: "Handrail — Bread Loaf, Single Slat",
    img: "/mouldings/handrail-bread-loaf-slat.png",
    sizes: ["68×68 mm", "68×42 mm"],
    desc: "Bread loaf handrail profile with single slat rebate for baluster fixing.",
  },
  {
    slug: "handrail-ladies-waist-flat-base",
    name: "Handrail — Ladies Waist, Flat Base",
    img: "/mouldings/handrail-ladies-waist-flat.png",
    sizes: ["68×68 mm", "68×42 mm"],
    desc: "Ladies waist handrail profile with flat base — the classic verandah rail.",
  },
  {
    slug: "handrail-ladies-waist-single-slat",
    name: "Handrail — Ladies Waist, Single Slat",
    img: "/mouldings/handrail-ladies-waist-slat.png",
    sizes: ["68×68 mm", "68×42 mm"],
    desc: "Ladies waist handrail profile with single slat rebate for baluster fixing.",
  },
  {
    slug: "handrail-ladies-waist",
    name: "Handrail — Ladies Waist",
    img: "/mouldings/handrail-ladies-waist.png",
    sizes: ["68×68 mm", "68×42 mm"],
    desc: "Ladies waist handrail profile, shaped for grip and weather run-off.",
  },
  {
    slug: "dowel-rods",
    name: "Dowel Rods",
    img: "/mouldings/dowel-rods.png",
    sizes: ["10 mm", "12 mm", "16 mm", "19 mm", "22 mm", "25 mm", "32 mm", "38 mm", "50 mm", "60 mm"],
    desc: "Hardwood dowel rods in ten diameters for joinery, furniture and craft use.",
  },
  {
    slug: "d-mould",
    name: "D-Mould",
    img: "/mouldings/d-mould.png",
    sizes: ["12×6 mm", "19×6 mm", "32×9 mm"],
    desc: "Half-round D-mould trim for edge detailing and finishing.",
  },
  {
    slug: "quad",
    name: "Quad",
    img: "/mouldings/quad.png",
    sizes: ["12×12 mm", "19×19 mm", "32×32 mm"],
    desc: "Quarter-round quad moulding for internal corner finishing.",
  },
  {
    slug: "angle-fillets",
    name: "Angle Fillets",
    img: "/mouldings/angle-fillets.png",
    sizes: ["12×12 mm", "19×19 mm", "32×32 mm"],
    desc: "Triangular angle fillets for corner support and finishing.",
  },
  {
    slug: "external-corner-mould",
    name: "External Corner Mould",
    img: "/mouldings/external-corner-mould.png",
    sizes: ["19×19 mm", "32×32 mm", "41×41 mm"],
    desc: "L-shaped external corner moulding for cladding and joinery edges.",
  },
  {
    slug: "cover-strips",
    name: "Cover Strips",
    img: "/mouldings/cover-strips.png",
    sizes: ["19×12 mm", "32×12 mm", "42×12 mm", "70×12 mm"],
    desc: "Flat hardwood cover strips for joints, gaps and finishing.",
  },
  {
    slug: "square-dressed",
    name: "Square Dressed",
    img: "/mouldings/square-dressed.png",
    sizes: ["12×12 mm", "19×19 mm", "32×32 mm"],
    desc: "Square dressed hardwood stock for general joinery and turning.",
  },
  {
    slug: "posts",
    name: "Posts",
    img: "/mouldings/posts.png",
    sizes: ["68×68 mm", "90×90 mm"],
    desc: "Solid hardwood posts for fencing, pergolas, verandahs and landscaping.",
  },
];

export const Swoosh = ({ className = "" }) => (
  <svg viewBox="0 0 600 70" className={className} aria-hidden="true" fill="none">
    <path
      d="M4 52 C 160 66, 340 60, 596 10 C 430 44, 220 54, 4 52 Z"
      fill="#692A1A"
    />
    <path
      d="M30 56 C 190 64, 360 56, 560 18"
      stroke="#8a3b24"
      strokeWidth="2"
      opacity="0.6"
    />
  </svg>
);
